import { Language } from '@/types';
import { FilterItem, ApiDefinition } from '@/types/api-definitions';
import { TagInfo, PreferredTagNames } from './types';

export const collectTagsInPreferredLanguage = (
  apiDefinitions: ApiDefinition[],
  language: Language,
): PreferredTagNames => {
  const nameBySlug = apiDefinitions.reduce((map, apiDefinition) => {
    const preferredContent = apiDefinition.authoredContent?.[language];
    if (!preferredContent) return map;

    return preferredContent.tags.reduce((tagMap, tag) => {
      if (!tagMap.has(tag.slug)) {
        tagMap.set(tag.slug, tag.name);
      }
      return tagMap;
    }, map);
  }, new Map<string, string>());

  return {
    nameBySlug,
    hasPreferredName: (slug: string) => nameBySlug.has(slug),
  };
};

export const collectAllTagsWithFallback = (
  apiDefinitions: ApiDefinition[],
  language: Language,
  preferredNames: PreferredTagNames,
): Map<string, TagInfo> => {
  return apiDefinitions.reduce((tagsBySlug, apiDefinition) => {
    if (!apiDefinition.authoredContent) return tagsBySlug;

    const content =
      apiDefinition.authoredContent[language] ||
      apiDefinition.authoredContent[Language.EN] ||
      apiDefinition.authoredContent[Language.NL];

    if (!content) return tagsBySlug;

    return content.tags.reduce((acc, tag) => {
      const existingTag = acc.get(tag.slug);
      const tagName = preferredNames.nameBySlug.get(tag.slug) ?? tag.name;

      if (existingTag) {
        existingTag.count += 1;
      } else {
        acc.set(tag.slug, {
          slug: tag.slug,
          name: tagName,
          count: 1,
          hasPreferredLanguage: preferredNames.hasPreferredName(tag.slug),
        });
      }
      return acc;
    }, tagsBySlug);
  }, new Map<string, TagInfo>());
};

export const convertToFilterItems = (tagsBySlug: Map<string, TagInfo>): FilterItem[] => {
  return Array.from(tagsBySlug.values())
    .map((tagInfo) => ({
      slug: tagInfo.slug,
      name: tagInfo.name,
      count: tagInfo.count,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};